import { useState, useEffect, useRef } from 'react';
import { AssessmentAnswers, ContactInfo } from '@/types/assessment';
import { useLocalAssessmentSave } from './useLocalAssessmentSave';
import {
  calculateOverallScore,
  getScoreLevel,
  getTrainingScoreLevel,
  calculateWeightedOverallScore,
} from '@/utils/assessmentUtils';

// Poids officiels (somme = 100) pour le diagnostic Training Transformation IA.
// Référence: spec / migration questionnaire `transformation-formation-ia`.
const TRAINING_AXIS_WEIGHTS: Record<string, number> = {
  'Stratégie': 15,
  'Offre': 12,
  'Commercial': 10,
  'Organisation': 10,
  'Processus': 10,
  'Ingénierie': 15,
  'Qualité': 8,
  'Outils': 12,
  'Change': 8,
};

interface UseAssessmentModalProps {
  assessmentType: 'ia-diagnostic' | 'training-transformation';
  totalSteps: number;
  startAtFirstQuestion?: boolean;
  isOpen: boolean;
}

export const useAssessmentModal = ({
  assessmentType,
  totalSteps,
  startAtFirstQuestion = false,
  isOpen,
}: UseAssessmentModalProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<AssessmentAnswers>({});
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    firstName: '',
    lastName: '',
    function: '',
    company: '',
    email: '',
    phone: '',
    sector: '',
    receiveReport: true,
  });
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { saveToLocal } = useLocalAssessmentSave();

  useEffect(() => {
    if (isOpen) {
      // Restaurer le brouillon depuis sessionStorage
      const draftKey = `assessment-draft-${assessmentType}`;
      const saved = sessionStorage.getItem(draftKey);
      if (saved) {
        try {
          const draft = JSON.parse(saved);
          if (draft.answers) setAnswers(draft.answers);
          if (draft.currentStep) setCurrentStep(draft.currentStep);
          else setCurrentStep(startAtFirstQuestion ? 1 : 0);
        } catch {
          setCurrentStep(startAtFirstQuestion ? 1 : 0);
        }
      } else {
        setCurrentStep(startAtFirstQuestion ? 1 : 0);
      }
    }
  }, [isOpen, startAtFirstQuestion, assessmentType]);

  const resetModal = () => {
    setCurrentStep(0);
    setAnswers({});
    setContactInfo({
      firstName: '',
      lastName: '',
      function: '',
      company: '',
      email: '',
      phone: '',
      sector: '',
      receiveReport: true,
    });
    const storageKeys = [
      'assessment-data-ia',
      'assessment-data-training',
      'assessment-data-learning',
      'assessment-data',
      'audit-results',
      'learning-strategy-results',
    ];
    storageKeys.forEach((key) => localStorage.removeItem(key));
    sessionStorage.removeItem(`assessment-draft-${assessmentType}`);
  };

  const scrollToTop = () => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  };

  const forceNextStep = () => {
    console.log('🚀 [FORCE NEXT STEP] Forcing transition from:', currentStep, 'to:', currentStep + 1);
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
      setTimeout(scrollToTop, 50);
    }
  };

  const nextStep = async (
    calculateScores: () => Record<string, { score: number; maxScore: number }>,
    scoredQuestions: any[],
    categories: string[],
  ) => {
    console.log('🔍 [DIAGNOSTIC] NextStep called:', {
      currentStep,
      totalSteps,
      assessmentType,
      condition: `currentStep === totalSteps - 3`,
      shouldSaveLocal: currentStep === totalSteps - 3,
      answersCount: Object.keys(answers).length,
    });

    if (currentStep < totalSteps - 1) {
      // Sauvegarder localement avant la page coordonnées (dernier "Suivant" du questionnaire)
      if (currentStep === totalSteps - 3) {
        const scores = calculateScores();
        const isTraining = assessmentType === 'training-transformation';
        const overallScore = isTraining
          ? calculateWeightedOverallScore(scores, TRAINING_AXIS_WEIGHTS)
          : calculateOverallScore(scores);
        const scoreLevel = isTraining
          ? getTrainingScoreLevel(overallScore)
          : getScoreLevel(overallScore);

        const dataToSave = {
          assessmentType,
          scores,
          answers,
          overallScore,
          scoreLevel,
          detailedAnswers: answers,
          questionsMetadata: {
            totalQuestions: scoredQuestions.length,
            categories,
            timestamp: new Date().toISOString(),
            conditionalQuestions: scoredQuestions.filter((q) => q.conditionalOn).length,
          },
        };

        saveToLocal(dataToSave);
      }

      setCurrentStep(currentStep + 1);
      setTimeout(scrollToTop, 50);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setTimeout(scrollToTop, 50);
    }
  };

  const updateAnswer = (questionId: number, answer: string | string[]) => {
    setAnswers((prev) => {
      const updated = { ...prev, [questionId]: answer };
      // Sauvegarde progressive dans sessionStorage pendant le diagnostic.
      const draftKey = `assessment-draft-${assessmentType}`;
      sessionStorage.setItem(draftKey, JSON.stringify({ answers: updated, currentStep }));
      return updated;
    });
  };

  const getProgress = () => {
    return (currentStep / (totalSteps - 1)) * 100;
  };

  return {
    currentStep,
    answers,
    contactInfo,
    modalContentRef,
    setContactInfo,
    resetModal,
    nextStep,
    forceNextStep,
    prevStep,
    updateAnswer,
    getProgress,
  };
};
